// FilterModal MULTISELECT stores selected options as { label, value } objects.
// The list API expects plain id arrays, so flatten them here at the request boundary.
const toIdList = (val: any) =>
    Array.isArray(val)
        ? val
              .map((v: any) => (v && typeof v === 'object' ? v.value : v))
              .filter((v: any) => v !== null && v !== undefined)
        : val;

export const preProcessPayload = (payload: any) => {
    let processedPayload = {
        ...payload,
        pageNo: (payload?.pageNo ?? 0) + 1,
    };
    if (!!payload?.searchText) {
        processedPayload.searchText = payload.searchText.trim();
    } else {
        processedPayload.searchText = '';
    }
    processedPayload.hubIds = toIdList(payload?.hubIds);
    processedPayload.vendorIds = toIdList(payload?.vendorIds);
    processedPayload.cities = toIdList(payload?.cities);
    processedPayload.zones = toIdList(payload?.zones);
    if (payload?.activeFilterCard?.filterStatus) {
        RIDER_FILTER_CARDS.forEach((card: any) => {
            if (
                payload?.activeFilterCard?.filterStatus === card?.filterStatus
            ) {
                processedPayload[card.type] = true;
            } else {
                processedPayload[card.type] = false;
            }
        });
        delete processedPayload.activeFilterCard;
    }

    return processedPayload;
};

export const EMP_ID_COLUMN = {
    name: 'Emp ID',
    mappingKey: 'empId',
    width: 146,
    chipVariant: 'chipEmpId',
    copyable: true,
    copyLabel: 'employee ID',
};

/** Deactivated view only: the rider's final day, which is also the resignation date Blue Dart
 * holds for them. Blank for anyone deactivated before it was being captured. */
export const LAST_WORKING_DATE_COLUMN = {
    name: 'Last Working Day',
    mappingKey: 'lastWorkingDate',
    width: 132,
    fixedWidth: true,
};

// `width` is each column's *base* — the narrowest it may render. The table
// fills its container and splits the leftover space equally between every
// column, except those marked `fixedWidth`, which stay at their base.
// `maxChars` hard-truncates the rendered text (the full value stays reachable
// through the cell tooltip); `ellipsis` leaves the clipping to CSS.
export const RIDER_MANAGEMENT_TABLE_COLUMNS = [
    {
        name: 'Name',
        mappingKey: 'name',
        width: 165,
        ellipsis: true,
    },
    {
        name: 'Phone',
        mappingKey: 'phoneNumber',
        width: 160,
        chipVariant: 'chipPhone',
        copyable: true,
        copyLabel: 'phone number',
    },
    {
        name: 'Vendor',
        mappingKey: 'vendor',
        width: 170,
        ellipsis: true,
        chipVariant: 'chipVendor',
    },
    {
        name: 'City',
        mappingKey: 'city',
        width: 135,
        ellipsis: true,
        chipVariant: 'chipCity',
    },
    {
        name: 'Zone',
        mappingKey: 'zone',
        // Capped at 5 characters, so the column is sized to exactly that and
        // pinned — extra width could only ever render as trailing whitespace.
        width: 86,
        fixedWidth: true,
        maxChars: 5,
        chipVariant: 'chipZone',
    },
    {
        name: 'Hub',
        mappingKey: 'hub',
        // Capped at 5 characters, so the column is sized to exactly that and
        // pinned — extra width could only ever render as trailing whitespace.
        width: 86,
        fixedWidth: true,
        maxChars: 5,
        chipVariant: 'chipHub',
    },
    {
        // MPC | PDA | IPDA | FESTIVE -- a closed set, longest 7 characters, so it
        // is pinned like Zone and Hub rather than given a share of the leftover
        // space it could not use.
        name: 'Emp Mode',
        mappingKey: 'employeeMode',
        width: 104,
        fixedWidth: true,
        maxChars: 7,
    },
    {
        // Held at its base by `fixedWidth`: the value is a single digit, so a
        // share of the leftover space would only ever render as a gap. 106px is
        // the floor set by the header label itself.
        name: 'Days Pending',
        mappingKey: 'daysPending',
        width: 106,
        fixedWidth: true,
        align: 'center',
    },
    // Pinned like Days Pending — two icon buttons can't use extra room, so its
    // share of the leftover space goes to the data columns instead.
    {
        name: 'Actions',
        mappingKey: 'action',
        actions: true,
        width: 96,
        fixedWidth: true,
        align: 'center',
    },
];

export const RIDER_FILTER_CARDS = [
    {
        name: 'Onboarded',
        type: 'downloadPending',
        subType: '',
        filterStatus: 'downloadPending',
        valueMappingKey: 'downloadPending',
    },
    {
        name: 'App Downloaded',
        type: 'appDownloaded',
        subType: '',
        filterStatus: 'appDownloaded',
        valueMappingKey: 'appDownloaded',
    },
    {
        name: 'Pending Vendor Verification',
        type: 'pendingVendorVerification',
        subType: '',
        filterStatus: 'pendingVendorVerification',
        valueMappingKey: 'pendingVendorVerification',
    },
    {
        name: 'BGV Pending',
        type: 'bgvPending',
        subType: '',
        filterStatus: 'bgvPending',
        valueMappingKey: 'bgvPending',
    },
    {
        name: 'Pending OPS Verification',
        type: 'pendingOpsVerification',
        subType: '',
        filterStatus: 'pendingOpsVerification',
        valueMappingKey: 'pendingOpsVerification',
    },
    {
        name: 'VendorRejected',
        type: 'vendorRejected',
        subType: '',
        filterStatus: 'vendorRejected',
        valueMappingKey: 'vendorRejected',
        /*subFilters: [
            {
                name: 'Vendor',
                type: 'vendorRejected',
                filterStatus: 'vendorRejected',
                valueMappingKey: 'vendorRejected',
            },
            {
                name: 'OPS',
                type: 'bdOpsRejected',
                filterStatus: 'bdOpsRejected',
                valueMappingKey: 'bdOpsRejected',
            },
            {
                name: 'System',
                type: 'systemRejected',
                filterStatus: 'systemRejected',
                valueMappingKey: 'systemRejected',
            },
        ],*/
    },
    {
        name: 'OPS Rejected',
        type: 'bdOpsRejected',
        subType: '',
        filterStatus: 'bdOpsRejected',
        valueMappingKey: 'bdOpsRejected',
        /*subFilters: [
            {
                name: 'Vendor',
                type: 'vendorRejected',
                filterStatus: 'vendorRejected',
                valueMappingKey: 'vendorRejected',
            },
            {
                name: 'OPS',
                type: 'bdOpsRejected',
                filterStatus: 'bdOpsRejected',
                valueMappingKey: 'bdOpsRejected',
            },
            {
                name: 'System',
                type: 'systemRejected',
                filterStatus: 'systemRejected',
                valueMappingKey: 'systemRejected',
            },
        ],*/
    },
    {
        name: 'Rejected',
        type: 'rejected',
        subType: '',
        filterStatus: 'rejected',
        valueMappingKey: 'rejected',
        /*subFilters: [
            {
                name: 'Vendor',
                type: 'vendorRejected',
                filterStatus: 'vendorRejected',
                valueMappingKey: 'vendorRejected',
            },
            {
                name: 'OPS',
                type: 'bdOpsRejected',
                filterStatus: 'bdOpsRejected',
                valueMappingKey: 'bdOpsRejected',
            },
            {
                name: 'System',
                type: 'systemRejected',
                filterStatus: 'systemRejected',
                valueMappingKey: 'systemRejected',
            },
        ],*/
    },
    {
        name: 'Emp ID Mapped',
        type: 'empIdMapped',
        subType: '',
        filterStatus: 'empIdMapped',
        valueMappingKey: 'empIdMapped',
    },
    {
        name: 'Deactivated',
        type: 'deactivated',
        subType: '',
        filterStatus: 'deactivated',
        valueMappingKey: 'deactivated',
    },
];
